"use client";

// Виджет «Календарь» (вынесен из дашборда IT): месячная сетка с дедлайнами
// (красные точки) и событиями Google-календаря (цветные точки), список
// ближайших дедлайнов, попап дня и настройка секретной iCal-ссылки.
// Самодостаточный: грузит свои данные (продукты/этапы/значения + события).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { confirmDialog } from "@/components/ui/confirm";
import type { IconType } from "@/lib/module-icons";
import { MapPin, Repeat } from "lucide-react";
import { IconCalendar, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight, IconClock, IconClose, IconEdit, IconLink, IconPlus, IconRoadmap, IconSettings, IconTrash} from "@/components/ui/icons";

import { api, API_BASE } from "@/lib/api";
import { EVENT_COLORS, RECUR_OPTIONS, WEEKDAYS_SHORT, type CalendarPerson, type NativeCalendarEvent, type RecurFreq } from "@/lib/it-tasks";
import type { TeamTask } from "@/lib/types";
import { Toggle } from "@/components/ui/toggle";
import { Select } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { INPUT_CLS, TEXTAREA_CLS } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Z } from "@/lib/z";

const WD = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const MONTHS_GEN = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// Позиция попапа РЯДОМ с кликнутым элементом (справа; слева — если не влезает).
// `assumedH` — ожидаемая высота для первичного позиционирования до замера.
function besidePos(rect: DOMRect, width: number, assumedH = 320): React.CSSProperties {
  const margin = 8;
  let left = rect.right + 8;
  if (left + width > window.innerWidth - 8) left = rect.left - width - 8;
  if (left < 8) left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
  // Стартуем у верха кликнутой ячейки; вверх сдвигаем РОВНО настолько, чтобы попап
  // (высотой assumedH) влез снизу — короткий остаётся у дня, высокий уезжает вверх.
  const top = Math.max(margin, Math.min(rect.top, window.innerHeight - margin - assumedH));
  return { position: "fixed", left, top, width, maxHeight: window.innerHeight - margin * 2, zIndex: Z.DROPDOWN };
}

// Попап рядом с кликнутым днём: после рендера меряет реальную высоту и
// перепозиционируется (сдвиг вверх только если упирается в низ экрана).
function BesidePopover({ rect, width, zIndex = 60, className, children }: {
  rect: DOMRect; width: number; zIndex?: number; className?: string; children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>(() => ({ ...besidePos(rect, width, 120), zIndex }));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setStyle({ ...besidePos(rect, width, el.offsetHeight), zIndex });
  }, [rect, width, zIndex, children]);
  return (
    <div ref={ref} className={className} style={style} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  );
}

type CalEvent = {
  date: string; time: string | null; end?: string | null; title: string;
  description?: string; location?: string; meet?: string;
};

function SquareBtn({ icon: Icon, onClick, title }: { icon: IconType; onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void; title?: string }) {
  return (
    <button type="button" onClick={onClick} data-tooltip={title}
      className="grid place-items-center rounded-[var(--radius-md)] border border-[var(--border)] text-[var(--textMuted)] transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
      style={{ width: 26, height: 26, boxShadow: "none" }}>
      <Icon size={13} />
    </button>
  );
}

// Аватар-фото публичен на origin API (/api/users/avatars/…): относительный путь
// резолвим через origin бэка, иначе грузится с фронта и 404 (битая картинка).
const AVATAR_ORIGIN = API_BASE.replace(/\/api\/?$/, "");
function avatarSrc(u?: string | null): string | undefined {
  if (!u) return undefined;
  if (/^https?:\/\//.test(u)) return u;
  return AVATAR_ORIGIN + (u.startsWith("/") ? u : `/${u}`);
}

// Аватар участника: фото, либо инициалы в цветном кружке (цвет — из имени).
function PartAvatar({ person, size = 16 }: { person: CalendarPerson; size?: number }) {
  const src = avatarSrc(person.avatar);
  if (src) {
    return <img src={src} alt={person.name} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  const initials = (person.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <span className="grid shrink-0 place-items-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), background: "var(--primary)" }}>{initials}</span>
  );
}

// Форма создания/редактирования нативного события (рядом с днём). Поля: название,
// дата, весь день / время, место, описание, цвет, участники (мультивыбор).
// Экспортируется наружу: этой же формой «Быстрые действия» кабинета назначают
// встречу — свою вторую форму встречи заводить незачем (02.09.2026).
export function EventEditor({ day, event, people, rect, onClose, onSaved, preselect, initial }: {
  day: string; event?: NativeCalendarEvent; people: CalendarPerson[]; rect: DOMRect; onClose: () => void; onSaved: () => void;
  /** Кого поставить участником у НОВОГО события. Календарь в профиле заводит
   *  событие человеку, чей профиль открыт, — иначе его пришлось бы каждый раз
   *  искать в списке участников вручную. */
  preselect?: string[];
  /** Готовое время для НОВОГО события: его приносит протягивание по сетке часов
   *  (календарь профиля). Без него новое событие заводится на весь день. */
  initial?: { time?: string; end?: string; allDay?: boolean };
}) {
  const editing = !!event;
  const [title, setTitle] = useState(event?.title ?? "");
  const [date, setDate] = useState(event?.base_date ?? event?.date ?? day);
  const [allDay, setAllDay] = useState(event?.all_day ?? initial?.allDay ?? true);
  const [time, setTime] = useState(event?.time ?? initial?.time ?? "");
  const [end, setEnd] = useState(event?.end ?? initial?.end ?? "");
  const [location, setLocation] = useState(event?.location ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [color, setColor] = useState(event?.color || EVENT_COLORS[0]);
  const [parts, setParts] = useState<string[]>(event?.participants.map((p) => p.id) ?? preselect ?? []);
  const [recurFreq, setRecurFreq] = useState<RecurFreq>(event?.recur_freq ?? "");
  const [recurInterval, setRecurInterval] = useState(event?.recur_interval ?? 1);
  const [recurUntil, setRecurUntil] = useState(event?.recur_until ?? "");
  const [recurByday, setRecurByday] = useState<number[]>(event?.recur_byday ?? []);
  const [pq, setPq] = useState("");
  const [saving, setSaving] = useState(false);
  const toggleDow = (d: number) => setRecurByday((b) => (b.includes(d) ? b.filter((x) => x !== d) : [...b, d].sort()));
  const inputCls = INPUT_CLS;

  const toggleP = (id: string) => setParts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const filtered = people.filter((p) => p.name.toLowerCase().includes(pq.trim().toLowerCase()));
  const selectedPeople = people.filter((p) => parts.includes(p.id));

  async function save() {
    if (!title.trim() || saving) return;
    setSaving(true);
    const body = {
      title: title.trim(), date, all_day: allDay, time: allDay ? "" : time, end: allDay ? "" : end,
      location, description, color, participant_ids: parts,
      recur_freq: recurFreq, recur_interval: Math.max(1, recurInterval || 1),
      recur_until: recurFreq ? recurUntil : "", recur_byday: recurFreq === "weekly" ? recurByday : [],
    };
    try { if (editing) await api.itprPatchEvent(event!.id, body); else await api.itprCreateEvent(body); onSaved(); }
    catch { /* тихо */ } finally { setSaving(false); }
  }
  async function del() {
    if (!event) return;
    if (!(await confirmDialog({ title: "Удалить событие?", confirmText: "Удалить", danger: true }))) return;
    try { await api.itprDeleteEvent(event.id); onSaved(); } catch { /* тихо */ }
  }

  return (
    <>
      <div className="fixed inset-0" style={{ zIndex: Z.DROPDOWN - 1 }} onClick={onClose} />
      {/* ⚠️ СЛОЙ ОКНА ВЫШЕ ЕГО ПОДЛОЖКИ. Было `zIndex={71}` при подложке на
          Z.DROPDOWN − 1 (10199): окно лежало ПОД ней, и любой клик — по полю,
          по списку участников — попадал в подложку и закрывал форму
          (Максим, 02.09.2026). Та же грабля, что 24.08 с командной панелью:
          «окно закрывается от клика» — это про слои, а не про обработчик. */}
      <BesidePopover rect={rect} width={340} zIndex={Z.DROPDOWN}
        className="flex flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] elev-modal">
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--border)] px-3.5 py-2.5">
          <span className="text-[12px] font-bold" >{editing ? "Событие" : "Новое событие"}</span>
          <button type="button" onClick={onClose} className="glass-flat rounded-[var(--radius-md)] p-0.5 text-[var(--textMuted)] hover:text-[var(--text)]"><IconClose size={14} /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto scrollbar-autohide p-3.5">
          <input className={inputCls} placeholder="Название события" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          <div className="flex items-center gap-2">
            <DatePicker value={date} onChange={(v) => setDate(v || date)} size="sm" clearable={false} className="flex-1" />
            <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-[var(--textMuted)]">
              <Toggle on={allDay} onChange={setAllDay} size="sm" /> весь день
            </span>
          </div>
          {!allDay && (
            <div className="flex items-center gap-2">
              <div className="flex-1"><TimePicker value={time} onChange={setTime} size="sm" /></div>
              <span className="text-[var(--textMuted)]">–</span>
              <div className="flex-1"><TimePicker value={end} onChange={setEnd} size="sm" /></div>
            </div>
          )}
          <input className={inputCls} placeholder="Место / ссылка (необязательно)" value={location} onChange={(e) => setLocation(e.target.value)} />
          <textarea className={`${TEXTAREA_CLS} min-h-[44px] resize-y`} rows={2} placeholder="Описание" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="flex items-center gap-1.5">
            <span className="field-label">Цвет</span>
            {EVENT_COLORS.map((c) => (
              <button type="button" key={c} onClick={() => setColor(c)} data-tooltip={c} style={{ background: c, borderColor: color === c ? "var(--text)" : "transparent", boxShadow: "none" }}
                className="h-5 w-5 rounded-full border-2 transition" />
            ))}
          </div>
          {/* Повтор */}
          <div className="space-y-1.5 rounded-[var(--radius-md)] border border-[var(--border)] p-2">
            <div className="flex items-center gap-1.5">
              <Repeat size={12} className="shrink-0 text-[var(--textMuted)]" />
              <div className="flex-1">
                <Select value={recurFreq} options={RECUR_OPTIONS} size="sm" className="w-full"
                  onChange={(v) => { const f = v as RecurFreq; setRecurFreq(f); if (f === "weekly" && recurByday.length === 0) setRecurByday([(new Date(date + "T00:00:00").getDay() + 6) % 7]); }} />
              </div>
            </div>
            {recurFreq && (
              <>
                <div className="flex items-center gap-1.5 text-[11px] text-[var(--textMuted)]">
                  <span>каждые</span>
                  <input type="number" min={1} value={recurInterval}
                    onChange={(e) => setRecurInterval(parseInt(e.target.value, 10) || 1)}
                    className="field-sm w-12 text-center" />
                  <span>{({ daily: "дн.", weekly: "нед.", monthly: "мес.", yearly: "г." } as Record<string, string>)[recurFreq]}</span>
                </div>
                {recurFreq === "weekly" && (
                  <div className="flex flex-wrap gap-1">
                    {WEEKDAYS_SHORT.map((w, i) => {
                      const on = recurByday.includes(i);
                      return (
                        <button type="button" key={i} onClick={() => toggleDow(i)} style={{ boxShadow: "none" }}
                          className={`h-6 w-7 rounded-[var(--radius-md)] border text-[11px] font-semibold transition ${on ? "seg seg-on" : "seg"}`}>{w}</button>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-[11px] text-[var(--textMuted)]">
                  <span className="shrink-0">до</span>
                  <DatePicker value={recurUntil} onChange={setRecurUntil} size="sm" placeholder="без конца" className="flex-1" />
                </div>
              </>
            )}
          </div>

          <div>
            <div className="field-label mb-1">Участники {parts.length > 0 ? `(${parts.length})` : ""}</div>
            {selectedPeople.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1">
                {selectedPeople.map((p) => (
                  <button type="button" key={p.id} onClick={() => toggleP(p.id)} data-tooltip="Убрать" style={{ boxShadow: "none" }}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--secondary)] py-0.5 pl-0.5 pr-1.5 text-[11px] hover:bg-[color-mix(in_srgb,#ef4444_18%,var(--secondary))]">
                    <PartAvatar person={p} size={15} /><span>{p.name}</span><IconClose size={9} />
                  </button>
                ))}
              </div>
            )}
            <input className={inputCls} placeholder="Поиск сотрудника…" value={pq} onChange={(e) => setPq(e.target.value)} />
            <div className="mt-1 max-h-36 space-y-0.5 overflow-y-auto scrollbar-autohide rounded-[var(--radius-md)] border border-[var(--border)] p-1">
              {filtered.map((p) => {
                const on = parts.includes(p.id);
                return (
                  <button type="button" key={p.id} onClick={() => toggleP(p.id)}
                    className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left text-[12px] hover:bg-[var(--hover)]">
                    <span className="grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border transition"
                      style={{ borderColor: on ? "#10b981" : "var(--border)", background: on ? "#10b981" : "transparent" }}>{on && <IconCheck size={11} className="text-white" />}</span>
                    <PartAvatar person={p} size={18} />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {p.department && <span className="shrink-0 text-[11px] text-[var(--textMuted)]">{p.department}</span>}
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="px-1.5 py-2 text-center text-[11px] text-[var(--textMuted)]">Никого не найдено</div>}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 border-t border-[var(--border)] px-3.5 py-2.5">
          <Button variant="primary" onClick={() => void save()} disabled={!title.trim() || saving}>
            {saving ? "Сохранение…" : editing ? "Сохранить" : "Создать"}
          </Button>
          <span className="flex-1" />
          {editing && (
            <Button title="Удалить событие" onClick={() => void del()}><IconTrash size={12} /> Удалить</Button>
          )}
        </div>
      </BesidePopover>
    </>
  );
}

// Дедлайны берём ТОЛЬКО с доски задач: модули «IT-проекты»/«Дашборд IT» удалены
// из портала (2026-07-28), слой дедлайнов их карточек и этапов убран вместе с ними.
export function ItprCalendarWidget({ canSettings = false, onOpenTask }: {
  canSettings?: boolean;
  onOpenTask?: (taskId: number, rect: DOMRect) => void;
}) {
  const [tasks, setTasks] = useState<TeamTask[]>([]);  // задачи доски (их due_date — дедлайны)
  const [cal, setCal] = useState<{ configured: boolean; events: CalEvent[]; error?: string }>({ configured: false, events: [] });
  const [gridEvents, setGridEvents] = useState<CalEvent[]>([]);
  const [nativeEvents, setNativeEvents] = useState<NativeCalendarEvent[]>([]);  // портальные события
  const [people, setPeople] = useState<CalendarPerson[]>([]);                    // справочник для участников
  const [canCreate, setCanCreate] = useState(false);
  const [editEvent, setEditEvent] = useState<{ event?: NativeCalendarEvent; date: string; rect: DOMRect } | null>(null);

  const [calOpen, setCalOpen] = useState(false);
  const [calAnchor, setCalAnchor] = useState<DOMRect | null>(null);
  const [calUrl, setCalUrl] = useState("");
  const [calSaving, setCalSaving] = useState(false);

  const [monthCursor, setMonthCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [dayPopup, setDayPopup] = useState<{ iso: string; rect: DOMRect } | null>(null);
  const [evOpen, setEvOpen] = useState<number | null>(null);
  const [natOpen, setNatOpen] = useState<number | null>(null);  // раскрытое нативное событие (по id)

  useEffect(() => {
    api.itprCalendarEvents().then(setCal).catch(() => {});
    api.listTasks("inbox").then(setTasks).catch(() => {});
    api.itprListPeople().then(setPeople).catch(() => {});
  }, []);

  // Месячная сетка: дни месяца + хвосты соседних до полных недель.
  const grid = useMemo(() => {
    const first = new Date(monthCursor.y, monthCursor.m, 1);
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(monthCursor.y, monthCursor.m + 1, 0).getDate();
    const rows = Math.ceil((lead + daysInMonth) / 7);
    const start = new Date(monthCursor.y, monthCursor.m, 1 - lead);
    const todayIso = isoLocal(new Date());
    return Array.from({ length: rows * 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      const iso = isoLocal(d);
      return { iso, day: d.getDate(), cur: d.getMonth() === monthCursor.m, today: iso === todayIso };
    });
  }, [monthCursor]);

  // События Google для отображаемого месяца (точки + попап дня).
  useEffect(() => {
    if (!grid.length || !cal.configured) return;
    api.itprCalendarEvents(grid[0].iso, grid[grid.length - 1].iso).then((r) => setGridEvents(r.events)).catch(() => {});
  }, [grid, cal.configured]);

  // Нативные (портальные) события для диапазона месяца.
  const loadNative = useCallback(() => {
    if (!grid.length) return;
    api.itprListNativeEvents(grid[0].iso, grid[grid.length - 1].iso)
      .then((r) => { setNativeEvents(r.events); setCanCreate(r.can_create); }).catch(() => {});
  }, [grid]);
  useEffect(() => { loadNative(); }, [loadNative]);

  // Нативные события по дате (для точек в ячейке и попапа дня).
  const nativeByDate = useMemo(() => {
    const m = new Map<string, NativeCalendarEvent[]>();
    for (const e of nativeEvents) { const a = m.get(e.date) ?? []; a.push(e); m.set(e.date, a); }
    return m;
  }, [nativeEvents]);

  const dueAll = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) if (t.due_date && t.status !== "done") s.add(t.due_date);
    return s;
  }, [tasks]);

  const dueByDate = useMemo(() => {
    type Due = { id: number; product: string; what: string };
    const m = new Map<string, Due[]>();
    const add = (date: string, item: Due) => { const a = m.get(date) ?? []; a.push(item); m.set(date, a); };
    for (const t of tasks) if (t.due_date && t.status !== "done") add(t.due_date, { id: t.id, product: t.title, what: "задача" });
    return m;
  }, [tasks]);

  // Ближайшие дедлайны (просроченные первыми).
  const upcoming = useMemo(() => {
    const todayIso = isoLocal(new Date());
    const items: { id: number; date: string; product: string; what: string; overdue: boolean }[] = [];
    for (const t of tasks) if (t.due_date && t.status !== "done") items.push({ id: t.id, date: t.due_date, product: t.title, what: "задача", overdue: t.due_date < todayIso });
    return items.sort((a, b) => (a.overdue !== b.overdue ? (a.overdue ? -1 : 1) : a.date.localeCompare(b.date))).slice(0, 12);
  }, [tasks]);

  async function openCalSettings(e: React.MouseEvent<HTMLButtonElement>) {
    setCalAnchor(e.currentTarget.getBoundingClientRect());
    try { const s = await api.itprGetCalendarSettings(); setCalUrl(s.ical_url); } catch { /* тихо */ }
    setCalOpen(true);
  }
  async function saveCalSettings() {
    setCalSaving(true);
    try {
      await api.itprSetCalendarSettings(calUrl.trim());
      setCalOpen(false);
      setCal(await api.itprCalendarEvents());
      if (grid.length) api.itprCalendarEvents(grid[0].iso, grid[grid.length - 1].iso).then((r) => setGridEvents(r.events)).catch(() => {});
    } catch { /* тихо */ } finally { setCalSaving(false); }
  }

  const fmtDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

  return (
    <div className="glass-panel flex h-full min-h-0 flex-col rounded-[var(--radius-lg)] border border-[var(--border)] p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid shrink-0 place-items-center rounded-[var(--radius-md)]" style={{ width: 30, height: 30, background: "color-mix(in srgb, #8b5cf6 16%, var(--card))", color: "#8b5cf6" }}>
          <IconCalendar size={16} />
        </span>
        <span className="truncate text-[12px] font-bold text-[var(--text)]" >Календарь</span>
        <span className="ml-auto flex items-center gap-1">
          <SquareBtn icon={IconChevronLeft} title="Предыдущий месяц"
            onClick={() => setMonthCursor((c) => ({ y: c.m === 0 ? c.y - 1 : c.y, m: (c.m + 11) % 12 }))} />
          <span className="min-w-[92px] text-center text-[12px] font-bold text-[var(--text)]" >{MONTHS[monthCursor.m]} {monthCursor.y}</span>
          <SquareBtn icon={IconChevronRight} title="Следующий месяц"
            onClick={() => setMonthCursor((c) => ({ y: c.m === 11 ? c.y + 1 : c.y, m: (c.m + 1) % 12 }))} />
        </span>
        {canSettings && <SquareBtn icon={IconSettings} onClick={(e) => void openCalSettings(e)} title="Подключить Google-календарь" />}
      </div>
      <div className="mb-1 grid grid-cols-7 gap-1">
        {WD.map((w) => <div key={w} className="text-center text-[11px] font-semibold text-[var(--textMuted)]">{w}</div>)}
      </div>
      <div className="mb-2 grid grid-cols-7 gap-1">
        {grid.map((d) => (
          <button type="button" key={d.iso}
            onClick={(e) => { setEvOpen(null); setNatOpen(null); setDayPopup({ iso: d.iso, rect: e.currentTarget.getBoundingClientRect() }); }}
            className={`glass-flat heat-cell flex cursor-pointer flex-col items-center rounded-[var(--radius-md)] py-0.5 ${d.today ? "heat-cell-active" : ""}`}
            data-tooltip={[dueAll.has(d.iso) ? "дедлайн" : "", new Set(gridEvents.map((e) => e.date)).has(d.iso) ? "событие" : ""].filter(Boolean).join(" · ") || undefined}
            style={{
              color: d.today ? "var(--primary)" : "var(--text)", fontWeight: d.today ? 700 : undefined,
              opacity: d.cur ? 1 : 0.35, ["--gc"]: d.today ? "var(--primary)" : "#64748b",
            } as unknown as React.CSSProperties}>
            <span className="text-[11px] font-semibold leading-tight tabular-nums">{d.day}</span>
            <span className="flex items-center gap-0.5" style={{ height: 5 }}>
              {dueAll.has(d.iso) && <span className="rounded-full" style={{ width: 5, height: 5, background: "#ef4444" }} />}
              {Array.from(new Set([
                ...(nativeByDate.get(d.iso) ?? []).map((e) => e.color || "var(--primary)"),
                ...gridEvents.filter((ev) => ev.date === d.iso).map(() => "var(--primary)"),
              ])).slice(0, 3).map((c, i) => (
                <span key={i} className="rounded-full" style={{ width: 5, height: 5, background: c }} />
              ))}
            </span>
          </button>
        ))}
      </div>

      <div className="field-label mb-1.5">Ближайшие дедлайны</div>
      <div className="-mx-4 -mb-4 max-h-[104px] space-y-1 overflow-y-auto px-4 pb-4 pt-3 scrollbar-autohide">
        {upcoming.map((u, i) => {
          const meta = u.what && u.what !== "дедлайн" ? u.what : "";
          return (
            <button type="button" key={i} onClick={(e) => onOpenTask?.(u.id, e.currentTarget.getBoundingClientRect())}
              style={{ ["--gc"]: "#64748b" } as React.CSSProperties}
              className="glass-flat heat-cell flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1 text-left">
              <span className="grid shrink-0 place-items-center rounded-[var(--radius-md)] px-1.5 text-[11px] font-bold tabular-nums"
                style={{ height: 20, color: u.overdue ? "#fff" : "#ef4444", background: u.overdue ? "#ef4444" : "color-mix(in srgb, #ef4444 14%, transparent)" }}
                data-tooltip={u.overdue ? "просрочено" : undefined}>
                {u.overdue ? "⚠" : ""}{fmtDate(u.date)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] font-semibold leading-tight">{u.product}</span>
              {meta && <span className="shrink-0 truncate text-[11px] text-[var(--textMuted)]">{meta}</span>}
            </button>
          );
        })}
        {upcoming.length === 0 && <div className="py-5 text-center text-[11px] text-[var(--textMuted)]">Дедлайнов не проставлено</div>}
      </div>

      {/* Попап дня */}
      {dayPopup && (() => {
        const dIso = dayPopup.iso, dRect = dayPopup.rect;
        const dEvents = gridEvents.filter((ev) => ev.date === dIso);
        const dDue = dueByDate.get(dIso) ?? [];
        const dNative = nativeByDate.get(dIso) ?? [];
        const dd = parseInt(dIso.slice(8, 10), 10);
        const mm = parseInt(dIso.slice(5, 7), 10) - 1;
        return (
          <>
            <div className="fixed inset-0 z-50" onClick={() => setDayPopup(null)} />
            <BesidePopover rect={dRect} width={320}
              className="overflow-y-auto scrollbar-autohide rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-3.5 elev-modal">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[12px] font-bold" >{dd} {MONTHS_GEN[mm]}</span>
                <span className="flex items-center gap-1">
                  {canCreate && (
                    <button type="button" data-tooltip="Добавить событие"
                      onClick={() => { setDayPopup(null); setEditEvent({ date: dIso, rect: dRect }); }}
                      className="glass-flat inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--textMuted)] hover:border-[var(--primary)] hover:text-[var(--primary)]"><IconPlus size={11} /> событие</button>
                  )}
                  <button type="button" onClick={() => setDayPopup(null)} className="glass-flat rounded-[var(--radius-md)] p-0.5 text-[var(--textMuted)] hover:text-[var(--text)]"><IconClose size={13} /></button>
                </span>
              </div>
              {/* Портальные события (приватные: участник/автор) */}
              {dNative.length > 0 && (
                <>
                  <div className="field-label mb-1.5 flex items-center gap-1.5">
                    <span className="rounded-full" style={{ width: 5, height: 5, background: "#8b5cf6" }} /> События
                  </div>
                  <div className="mb-2.5 space-y-1.5">
                    {dNative.map((ev) => {
                      const open = natOpen === ev.id;
                      const isLink = !!ev.location && /^https?:\/\//.test(ev.location);
                      const hasExtra = !!ev.location || !!ev.description || ev.participants.length > 0;
                      return (
                      <div key={ev.id} className="group overflow-hidden rounded-[var(--radius-lg)] border bg-[color-mix(in_srgb,var(--card)_70%,var(--bg))]"
                        style={{ borderColor: `color-mix(in srgb, ${ev.color || "#8b5cf6"} 45%, var(--border))` }}>
                        {/* Свёрнутая шапка: бейдж времени + название + шеврон (как у Google-событий) */}
                        <button type="button" onClick={() => setNatOpen(open ? null : ev.id)} data-tooltip={open ? "Свернуть" : "Подробнее"}
                          className="glass-flat flex w-full cursor-pointer items-center gap-2 rounded-[var(--radius-lg)] px-2.5 py-1.5 text-left text-[12px] transition hover:bg-[var(--hover)]">
                          <span className="shrink-0 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px] font-bold tabular-nums"
                            style={{ color: ev.color || "#8b5cf6", background: `color-mix(in srgb, ${ev.color || "#8b5cf6"} 14%, transparent)` }}>
                            {ev.all_day ? "весь день" : (ev.time ?? "—")}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-semibold text-[var(--text)]">{ev.title || "Без названия"}</span>
                          {ev.recurring && <Repeat size={11} className="shrink-0 text-[var(--textMuted)]" />}
                          {isLink && <IconLink size={11} className="shrink-0 text-[var(--primary)]" />}
                          {ev.can_edit && (
                            <span role="button" tabIndex={0} data-tooltip="Редактировать"
                              onClick={(e) => { e.stopPropagation(); setDayPopup(null); setEditEvent({ event: ev, date: ev.date, rect: dRect }); }}
                              className="shrink-0 cursor-pointer text-[var(--textMuted)] opacity-0 transition hover:text-[var(--primary)] group-hover:opacity-100"><IconEdit size={12} /></span>
                          )}
                          {hasExtra && (open ? <IconChevronDown size={12} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={12} className="shrink-0 text-[var(--textMuted)]" />)}
                        </button>
                        {/* Раскрытая доп.инфо: ссылка-кнопка / место / описание / участники */}
                        {open && hasExtra && (
                          <div className="space-y-1.5 px-2.5 pb-2 pl-[26px] pt-0.5">
                            {isLink ? (
                              <a href={ev.location} target="_blank" rel="noreferrer"
                                className="glass-btn-primary inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90">
                                <IconLink size={12} /> Подключиться
                              </a>
                            ) : ev.location ? (
                              <div className="flex items-center gap-1 text-[11px] text-[var(--textMuted)]"><MapPin size={11} className="shrink-0" /> {ev.location}</div>
                            ) : null}
                            {ev.description && <div className="whitespace-pre-line text-[11px] leading-snug text-[var(--textMuted)]">{ev.description}</div>}
                            {ev.participants.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1">
                                {ev.participants.map((p) => (
                                  <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-[var(--secondary)] py-0.5 pl-0.5 pr-1.5" data-tooltip={p.name}>
                                    <PartAvatar person={p} size={15} /><span className="text-[11px] text-[var(--text)]">{p.name}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </>
              )}
              {dDue.length > 0 && (
                <>
                  <div className="field-label mb-1.5 flex items-center gap-1.5">
                    <span className="rounded-full" style={{ width: 5, height: 5, background: "#ef4444" }} /> Дедлайны
                  </div>
                  <div className="mb-2.5 space-y-1.5">
                    {dDue.map((u, i) => (
                      <Button className="h-auto w-full justify-start py-2 text-left" key={i}
                        onClick={() => { const rect = dayPopup.rect; setDayPopup(null); onOpenTask?.(u.id, rect); }}>
                        <span className="min-w-0 flex-1 truncate">{u.product}</span>
                        {u.what && u.what !== "дедлайн карточки" && <span className="shrink-0 text-[11px] text-[var(--textMuted)]">{u.what}</span>}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              {dEvents.length > 0 && (
                <>
                  <div className="field-label mb-1.5 flex items-center gap-1.5">
                    <span className="rounded-full" style={{ width: 5, height: 5, background: "#0ea5e9" }} /> Google-календарь
                  </div>
                  <div className="space-y-1.5">
                    {dEvents.map((ev, i) => {
                      const open = evOpen === i;
                      const link = ev.meet || (ev.location && /^https?:\/\//.test(ev.location) ? ev.location : "");
                      const place = ev.location && !/^https?:\/\//.test(ev.location) ? ev.location : "";
                      return (
                        <div key={i} className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[color-mix(in_srgb,var(--card)_70%,var(--bg))]">
                          <button type="button" onClick={() => setEvOpen(open ? null : i)}
                            className="glass-flat flex w-full cursor-pointer items-center gap-2 rounded-[var(--radius-lg)] px-2.5 py-1.5 text-left text-[12px] transition hover:bg-[var(--hover)]"
                            data-tooltip={open ? "Свернуть" : "Подробнее"}>
                            <span className="shrink-0 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px] font-bold tabular-nums"
                              style={{ color: "var(--primary)", background: "color-mix(in srgb, var(--primary) 14%, transparent)" }}>
                              {ev.time ?? "весь день"}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-semibold">{ev.title}</span>
                            {open ? <IconChevronDown size={12} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={12} className="shrink-0 text-[var(--textMuted)]" />}
                          </button>
                          {open && (
                            <div className="space-y-1.5 px-2.5 pb-2 pt-0.5 text-[11px]">
                              {ev.time && <div className="flex items-center gap-1 text-[var(--textMuted)]"><IconClock size={12} className="shrink-0" />{ev.time}{ev.end ? `–${ev.end}` : ""}</div>}
                              {place && <div className="flex items-center gap-1 text-[var(--textMuted)]"><IconRoadmap size={12} className="shrink-0" />{place}</div>}
                              {ev.description && <div className="line-clamp-4 whitespace-pre-line leading-snug text-[var(--textMuted)]">{ev.description}</div>}
                              {link && (
                                <a href={link} target="_blank" rel="noreferrer"
                                  className="glass-btn-primary inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90">
                                  Подключиться ↗
                                </a>
                              )}
                              {!place && !ev.description && !link && !ev.time && <div className="text-[var(--textMuted)] opacity-60">Дополнительной информации нет</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {dDue.length === 0 && dEvents.length === 0 && dNative.length === 0 && (
                <div className="py-4 text-center text-[11px] text-[var(--textMuted)]">
                  На этот день ничего нет{canCreate ? " — добавьте событие" : ""}
                </div>
              )}
            </BesidePopover>
          </>
        );
      })()}

      {/* Создание/редактирование портального события */}
      {editEvent && (
        <EventEditor day={editEvent.date} event={editEvent.event} people={people} rect={editEvent.rect}
          onClose={() => setEditEvent(null)} onSaved={() => { setEditEvent(null); loadNative(); }} />
      )}

      {/* Настройка Google-календаря (секретная iCal-ссылка) */}
      {calOpen && calAnchor && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setCalOpen(false)} />
          <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-3.5 elev-modal" style={besidePos(calAnchor, 400)} onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold">Google-календарь</span>
              <button type="button" onClick={() => setCalOpen(false)} className="glass-flat rounded-[var(--radius-md)] p-0.5 text-[var(--textMuted)] hover:text-[var(--text)]"><IconClose size={13} /></button>
            </div>
            <div className="mb-2 text-[11px] leading-snug text-[var(--textMuted)]">
              Google Календарь → Настройки календаря → «Закрытый адрес в формате iCal» — вставьте ссылку (…basic.ics). Чтение каждые 10 мин, ничего в календарь не пишем.
            </div>
            <input value={calUrl} onChange={(e) => setCalUrl(e.target.value)} placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
              className="field-sm mb-2" />
            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={() => void saveCalSettings()} disabled={calSaving}>
                {calSaving ? "Сохранение…" : "Сохранить"}
              </Button>
              {calUrl && (
                <Button onClick={() => setCalUrl("")}>Очистить</Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
